import { queryKeys } from '@/constant/common'
import { CONFIG_ROUTES } from '@/constant/route'
import { useConfigUI } from '@/features/config/context'
import { DeleteType } from '@/types/hydraulic'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import useTranslation from '@/hooks/useTranslation'
import { getApiErrorMessage } from '@/helpers'
import {
  deleteFlowRuleWithChildren,
  type FlowRuleChildren,
} from '../flowRuleCascade'

/**
 * SCRUM-1032: a rule cannot be deleted while its valves and sensors are still
 * on it — the API refuses. They go first, in that order, so one confirmation
 * removes the whole thing rather than leaving the user to unpick it.
 */
async function deleteRule(
  interfaceId: string,
  id: string,
  children?: FlowRuleChildren,
): Promise<void> {
  await deleteFlowRuleWithChildren(interfaceId, id, children)
}
const useDeleteFlowRule = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { closeModal } = useConfigUI()
  const navigate = useNavigate()

  // The valves and sensors went with the rule, and the pipe's own counts are
  // read from those lists.
  const refreshCascade = () => {
    queryClient.invalidateQueries([queryKeys.flowRule])
    queryClient.invalidateQueries([queryKeys.flowValve])
    queryClient.invalidateQueries([queryKeys.flowSensor])
    queryClient.invalidateQueries([queryKeys.hydraulic])
  }

  return useMutation(
    (payload: DeleteType & { children?: FlowRuleChildren }) =>
      deleteRule(payload.interfaceId, payload.id, payload.children),
    {
      onSuccess: () => {
        refreshCascade()
        toast.success(t('deleteFlowRuleSuccess'))
        navigate(CONFIG_ROUTES.dataConfig.to)
        closeModal()
      },
      onError: (error: AxiosError<any>) => {
        // Some valves may have gone before the cascade stopped.
        refreshCascade()
        toast.error(getApiErrorMessage(error))
        closeModal()
      },
    },
  )
}

export default useDeleteFlowRule
